import React, { useState, useEffect, forwardRef, useRef } from 'react';

import { doc, onSnapshot } from 'firebase/firestore';
import db from '@services/firebase-service';
import { logToCloudWatch } from '@services/logger.service';
import { stopEngagement } from '@services/youtube-platform.service';
import { useStreamData } from '@lib/hooks/useStreamData';
import { handleEngagement } from '@services/interactions-service';
import { getBarLayout } from '@services/utils.service';
import AnimatedQuizOption from '@components/Features/Quiz/AnimatedQuizOption';
import { getQuizImageColors } from '@components/Presentation/main/PresentationBody/InteractiveInteractions/Quiz';
import ImageLoader from '@atoms/new/imageLoader';
import useQuizSounds from '@components/Features/Quiz/useQuizSounds';
import useTimer from '@lib/hooks/useTimer';
import { getOptionsGridClasses } from '@components/Features/Quiz/herlper';

const Quiz = forwardRef(({
  streamId,
  platformType,
  currentRosFeature,
  currentRosResult,
  interactionType,
  setCurrentFeatureId,
  startInteraction,
  setShowRosModel,
  moderationModeRef,
  activeInteractionRef,
  updateFSMeta,
  setShowNotification,
  showAnswerMap = {},
  showQuizAnswers,
  isAnalytics,
  analyticsData,
  setStartTime,
  setTotalResponders,
  isMuted,
  layoutMode,
  interactionState,
  onQuizDone
}, ref) => {
  const isQuizSoundEnabled = !isMuted && currentRosFeature?.setting?.isQuizSoundEnabled;
  const isPreviouslyDone = showAnswerMap[currentRosFeature?.id] || false;
  const {
    createBackgroundMusic,
    playCountdownSound,
    playFinalCountdownSound,
    playOptionAppearSound,
    playStartSound,
    playTimeUpSound,
    stopBackgroundMusic,
    pauseBackgroundMusic,
    resumeBackgroundMusic
  } = useQuizSounds({
    isMuted: !(isQuizSoundEnabled ?? true),
    startInteraction: interactionState === 'running' && !isPreviouslyDone
  });
  const {
    count: quizCount,
    startTimer: quizStartTimer,
    resetTimer: quizResetTimer,
    stopTimer: quizStopTimer,
    pauseTimer: quizPauseTimer
  } = useTimer({
    initialCount: isPreviouslyDone ? 0 : 4,
    onTick: (counter) => {
      if (counter > 0) {
        if (counter === 1) {
          playFinalCountdownSound();
        } else {
          playCountdownSound();
        }
      }

      if (counter === 0) {
        timeLeftStartTimer();
      }
    },
    isEnabled: !!currentRosFeature.id
  });
  const quizTimerDuration = currentRosFeature?.setting?.quizTimerDuration ?? 15;

  const {
    count: timeLeft,
    startTimer: timeLeftStartTimer,
    resetTimer: timeLeftResetTimer,
    stopTimer: timeLeftStopTimer,
    pauseTimer: timeLeftPauseTimer
  } = useTimer({
    initialCount: isPreviouslyDone ? 0 : quizTimerDuration,
    onTick: (counter) => {
      if (counter === (quizTimerDuration - 2)) {
        createBackgroundMusic();
      }

      if (counter === 4) {
        stopBackgroundMusic();
      }

      if (counter <= 4 && counter > 0) {
        playCountdownSound();
      }
    },
    onComplete: () => {
      playTimeUpSound();
      handleStopInteraction();
      onQuizDone();
      showQuizAnswers(currentRosFeature.id);
    },
    isEnabled: !!currentRosFeature.id
  });

  const {
    firestoreUnsub,
    setFirestoreUnsub,
    setFeatureId
  } = useStreamData(s => ({
    firestoreUnsub: s.firestoreUnsub,
    setFirestoreUnsub: s.setFirestoreUnsub,
    setFeatureId: s.setFeatureId
  }));

  const { isResult, isRos } = interactionType;
  const [quizId, setQuizId] = useState('');

  const sessionId = localStorage.getItem('sessionId');
  const [interactionResponse, setInteractionResponse] = useState(currentRosFeature.options);
  const [currentSettings, setCurrentSettings] = useState(currentRosFeature.setting);
  const [currentColors, setCurrentColors] = useState(currentRosFeature.color);
  const stopQuiz = async () => {
    if (quizId) {
      await stopEngagement(streamId, 'quiz', { engagementId: quizId, platformType, sessionId });
      setQuizId('');
      setFeatureId('');
      if (firestoreUnsub.unSub) {
        firestoreUnsub.unSub();
      }
    }
  };

  const setupQuiz = ({
    id, current, noOfResponders
  }) => {
    setInteractionResponse(current);
    setCurrentFeatureId(id);
    setQuizId(id);
    setFeatureId(id);
    setTotalResponders(noOfResponders);
  };

  const handleQuiz = async ({ interactionId = null, interactionResult = [] }) => {
    const response = await handleEngagement({ streamId, platformType, currentRosFeature, interactionId });
    if (response.status && response.entity) {
      const interactionDetails = {
        id: response.entity.engagementId,
        current: interactionResult,
        type: currentRosFeature.type,
        noOfResponders: response.entity.noOfResponders || 0,
      };
      setupQuiz(interactionDetails);
      setCurrentSettings(currentRosFeature.setting || currentRosFeature.settings);
      updateFSMeta({ activeInteraction: interactionDetails });
    }
  };

  const handleStartInteraction = () => {
    if (currentRosResult && currentRosResult.length > 0) {
      handleQuiz({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult[0].options });
    } else {
      handleQuiz({ interactionId: null, interactionResult: currentRosFeature.options });
    }
    setStartTime(Date.now());
  };

  const handleStopInteraction = () => {
    stopQuiz();
    setQuizId(null);
    if (currentRosResult && currentRosResult[0]?.options) {
      setInteractionResponse(currentRosResult[0]?.options);
    }
  };

  const manageQuiz = () => {
    if (!isResult && startInteraction) {
      handleStartInteraction();
    } else if (quizId) {
      handleStopInteraction();
    } else if (currentRosResult && currentRosResult.length > 0) {
      setInteractionResponse(currentRosResult[0].options);
      setTotalResponders(currentRosResult[0].noOfResponders || 0);
    }
  };

  useEffect(() => {
    if (!currentRosFeature.id) return;

    setShowRosModel(false);
    manageQuiz();
  }, [currentRosFeature.id, startInteraction]);

  const unsubscribeFromFirestore = () => {
    if (firestoreUnsub.unsub) {
      firestoreUnsub.unsub();
    }
  };

  useEffect(() => {
    if (currentRosFeature.setting) {
      setCurrentSettings(currentRosFeature.setting);
    }
  }, [currentRosFeature.setting]);

  useEffect(() => {
    if (currentRosFeature.color) {
      setCurrentColors(currentRosFeature.color);
    }
  }, [currentRosFeature.color]);

  useEffect(() => {
    if (currentRosFeature.options) {
      setInteractionResponse(currentRosFeature.options);
    }
  }, [currentRosFeature.options]);

  useEffect(() => {
    if (quizId) {
      const logData = {
        streamId,
        interactionType: 'quiz',
        interactionId: quizId
      };

      setFirestoreUnsub({
        unSub: onSnapshot(doc(db, 'streams', streamId, 'quiz', quizId), (document) => {
          if (document.exists()) {
            let { data } = document.data();
            if (data) {
              setInteractionResponse(data);
              setShowNotification(false);
            }
            setTotalResponders(document.data().noOfResponders || 0);
          }
        }, (error) => logToCloudWatch('Failed to subscribe to firestore', { ...logData, error }, 'ERROR'))
      });
    }
    return () => {
      unsubscribeFromFirestore();
    };
  }, [quizId]);
  const isShowAnswerVisible = showAnswerMap[currentRosFeature.id] || false;

  const data = interactionResponse || [];

  const relativeMax = Math.max(...data?.map((option) => option.contribution ? parseInt(option.contribution) : 0));

  const showResponses = currentSettings?.showResponses !== false || !startInteraction;

  const [fontSize, setFontSize] = useState('text-4xl');
  const [roundedCorner, setRoundedCorner] = useState('rounded-[40px]');
  const [parentContainer, setParentContainer] = useState({ height: 'h-full', });
  const [optionNumContainer, setOptionNumContainer] = useState('w-[100px]');
  const [imgColors, setImgColors] = useState(null);

  useEffect(() => {
    if (!data) return;
    const {
      fontSize,
      roundedCorner,
      optionNumContainer,
      parentContainer,
    } = getBarLayout(data.length);
    setFontSize(fontSize);
    setRoundedCorner(roundedCorner);
    setOptionNumContainer(optionNumContainer);
    setParentContainer(parentContainer);
  }, [data]);

  const quizImage = currentSettings?.quizSlideImageSrc;
  const quizImagePath = `${process.env.NEXT_PUBLIC_BUCKET_URL}${quizImage}`;

  useEffect(() => {
    if (!quizImage) return;
    getQuizImageColors(quizImagePath)
      .then(colors => {
        if (!colors) return;
        setImgColors(colors);
      });
  }, [quizImage]);

  const lastInteractionState = useRef(interactionState);

  useEffect(() => {
    if (!interactionState) return;

    if (interactionState === 'stopped') {
      quizStopTimer();
      timeLeftStopTimer();
      quizResetTimer();
      timeLeftResetTimer();
      stopBackgroundMusic();
    } else if (interactionState === 'paused') {
      quizPauseTimer();
      timeLeftPauseTimer();
      pauseBackgroundMusic();
    } else if (interactionState === 'running') {
      if (lastInteractionState.current === 'paused') {
        if (quizCount > 0) {
          quizStartTimer();
        } else {
          timeLeftStartTimer();
        }
        if (timeLeft > 4 && timeLeft <= 13) {
          resumeBackgroundMusic();
        }
      } else {
        // Fresh start (gate click, reset/replay): quizCount may still hold the
        // stale post-completion value in this commit, so reset both timers
        // explicitly instead of gating on it
        playStartSound();
        quizResetTimer();
        timeLeftResetTimer();
        quizStartTimer();
      }
    }
    lastInteractionState.current = interactionState;

    return () => {
      quizStopTimer();
      timeLeftStopTimer();
      stopBackgroundMusic();
    };
  }, [interactionState]);

  useEffect(() => {
    if (!currentRosFeature.id) return;

    return () => {
      quizStopTimer();
      timeLeftStopTimer();
      stopBackgroundMusic();
    };
  }, [currentRosFeature.id]);

  return (
    <div className={'flex flex-col size-full relative'}>
      {(quizCount > 0 && !['stopped', 'queued'].includes(interactionState)) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent">
          <div className="text-9xl font-semibold leading-none"
            style={{
              color: currentColors?.quizHeadingColor ?? currentColors?.multipleChoiceHeadingColor ?? '#fff'
            }}
          >
            {quizCount}
          </div>
        </div>
      )}
      <div className="py-2 rounded-full text-[30px] font-semibold text-right"
        style={{
          color: currentColors?.quizHeadingColor ?? currentColors?.multipleChoiceHeadingColor ?? '#fff'
        }}
      >
        Time left: {timeLeft}s
      </div>
      <div className={`${layoutMode === 'square' ? 'flex-col' : ''} flex gap-2 size-full relative`}>
        {quizImage && (
          <div
            style={{
              backgroundImage: quizImage ? `radial-gradient(circle, ${imgColors?.vibrant}, ${imgColors?.muted})` : undefined,
            }}
            className={`size-full ${(quizCount === 0 || ['stopped'].includes(interactionState)) ? 'flex' : 'hidden'} flex-[2]  mr-2  rounded-3xl ${layoutMode === 'square' ? 'h-2/5 w-[98%] mx-auto' : 'h-full w-2/6'}`}>
            <div className='size-full overflow-hidden my-auto'>
              <ImageLoader
                src={quizImagePath}
                className='size-full flex-1 object-contain'
              />
            </div>
          </div>
        )}
        <div className={`flex-[4] h-full justify-center overflow-hidden grid gap-4 transition-transform ease-in-out duration-1000 ${getOptionsGridClasses({
          optionCount: data?.length || 0,
          hasImage: !!quizImage,
          layoutMode,
        })}`}>
          {data.map((option, index) => (
            <AnimatedQuizOption
              key={option.key || option.id || `option-${index}`}
              option={option}
              index={index}
              timeLeft={['stopped'].includes(interactionState) ? 0 : timeLeft}
              animationStartTime={quizTimerDuration - 1}
              playOptionAppearSound={playOptionAppearSound}
              showResponses={showResponses}
              currentColors={currentColors}
              currentSettings={currentSettings}
              relativeMax={relativeMax}
              isShowAnswerVisible={isShowAnswerVisible}
              totalOptionCount={data?.length}
              fontSize={fontSize}
              roundedCorner={roundedCorner}
              optionNumContainer={optionNumContainer}
              parentContainer={parentContainer}
              hasImage={!!quizImage}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

export default Quiz;
